/* eslint-disable react-hooks/set-state-in-effect */
import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Modal } from 'antd';
import { useNavigate } from 'react-router-dom';
import { runAboutQuery } from '@/pages/about/about.query';
import ReleaseNoteContent, { hasReleaseNoteSections } from '@/pages/about/release-note-content';

const isWelcomeTourPromptDue = () => {
    if (typeof window === 'undefined') {
        return false;
    }

    return !localStorage.getItem('tourIgnored') && !sessionStorage.getItem('tourDismissed');
};

const getReleaseDismissalKey = (releaseNote) => `dms-release-dismissed:${releaseNote.clientVersion}:${releaseNote.releaseNoteId}`;

const isReleaseDismissed = (releaseNote) => {
    if (typeof window === 'undefined') {
        return false;
    }

    const dismissalKey = getReleaseDismissalKey(releaseNote);
    return localStorage.getItem(dismissalKey) === 'true'
        || sessionStorage.getItem(dismissalKey) === 'true';
};

const dismissRelease = (releaseNote) => {
    if (typeof window !== 'undefined') {
        localStorage.setItem(getReleaseDismissalKey(releaseNote), 'true');
    }
};

const dismissReleaseForSession = (releaseNote) => {
    if (typeof window !== 'undefined') {
        sessionStorage.setItem(getReleaseDismissalKey(releaseNote), 'true');
    }
};

const getTextLines = (text) => (text || '')
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

const splitDetails = (details) => {
    return getTextLines(details).reduce((acc, line) => {
        const bulletMatch = line.match(/^[-*]\s+(.+)$/);
        if (bulletMatch) {
            acc.bullets.push(bulletMatch[1]);
        } else {
            acc.paragraphs.push(line);
        }

        return acc;
    }, { bullets: [], paragraphs: [] });
};

const ReleaseNotificationModal = () => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const isLogin = useSelector(state => state.userDetails.isLogin);
    const isTourOpen = useSelector(state => state.dashboardPage.isTourOpen);
    const isTourPromptOpen = useSelector(state => state.dashboardPage.isTourPromptOpen);
    const [releaseNote, setReleaseNote] = useState(null);
    const [open, setOpen] = useState(false);
    const [loaded, setLoaded] = useState(false);

    useEffect(() => {
        if (!isLogin) {
            setLoaded(false);
            setReleaseNote(null);
            setOpen(false);
        }
    }, [isLogin]);

    useEffect(() => {
        if (!isLogin || loaded) {
            return;
        }

        setLoaded(true);
        runAboutQuery(dispatch, 'getReleaseNotification')
            .then(data => {
                if (data?.releaseNoteId && !isReleaseDismissed(data)) {
                    setReleaseNote(data);
                }
            })
            .catch(() => {});
    }, [isLogin, loaded, dispatch]);

    useEffect(() => {
        if (releaseNote && !isTourOpen && !isTourPromptOpen && !isWelcomeTourPromptDue()) {
            setOpen(true);
        }
    }, [isTourOpen, isTourPromptOpen, releaseNote]);

    if (!releaseNote) {
        return null;
    }

    const summaryLines = getTextLines(releaseNote.summary);
    const detailContent = splitDetails(releaseNote.details);
    const hasStructuredContent = hasReleaseNoteSections(releaseNote.notificationSections);

    const dismiss = () => {
        dismissRelease(releaseNote);
        setOpen(false);
        setReleaseNote(null);
    };

    // Closing with the X is temporary for this login, but it must still disarm
    // the loaded notice across the Help centre's full-page links. The permanent
    // dismissal button below continues to use localStorage for this version.
    const closeForSession = () => {
        dismissReleaseForSession(releaseNote);
        setOpen(false);
        setReleaseNote(null);
    };

    return (
        <Modal
            open={open}
            className="release-notification-modal"
            title={`New Version Release Notice: ${releaseNote.clientVersion}`}
            width={760}
            onCancel={closeForSession}
            footer={[
                <Button
                    key="view"
                    onClick={() => {
                        closeForSession();
                        navigate('/release-notes');
                    }}
                >
                    View all release notes
                </Button>,
                <Button key="dismiss" type="primary" onClick={dismiss}>
                    Close and don&apos;t show this version again
                </Button>,
            ]}
        >
            <div className="release-notification-content">
                <h2>{releaseNote.title}</h2>

                {hasStructuredContent ? (
                    <ReleaseNoteContent
                        sections={releaseNote.notificationSections}
                        classNamePrefix="release-notification"
                    />
                ) : (
                    <>
                        {summaryLines.length > 0 && (
                            <section className="release-notification-section">
                                <h3>Highlights</h3>
                                {summaryLines.map((line, index) => <p key={`${index}-${line}`}>{line}</p>)}
                            </section>
                        )}

                        {(detailContent.paragraphs.length > 0 || detailContent.bullets.length > 0) && (
                            <section className="release-notification-section">
                                <h3>What changed</h3>
                                {detailContent.paragraphs.map((line, index) => <p key={`${index}-${line}`}>{line}</p>)}
                                {detailContent.bullets.length > 0 && (
                                    <ul className="release-notification-list">
                                        {detailContent.bullets.map((line, index) => <li key={`${index}-${line}`}>{line}</li>)}
                                    </ul>
                                )}
                            </section>
                        )}
                    </>
                )}
            </div>
        </Modal>
    );
};

export default ReleaseNotificationModal;
